import type { EscalationLevelInput } from '@chukua/common';
import { Button, Table } from 'antd';
import { BellRing, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { buildEscalationLevelColumns } from './build-escalation-level-columns';
import { EscalationLevelFormDrawer } from './EscalationLevelFormDrawer';
import type { EscalationLevel } from '../../../reference-data.api';

interface EscalationLevelsPanelProps {
  levels: EscalationLevel[];
  loading: boolean;
  saveError: string | null;
  saving: boolean;
  onCreate: (input: EscalationLevelInput) => Promise<boolean>;
  onUpdate: (id: string, input: EscalationLevelInput) => Promise<boolean>;
  onDeactivate: (row: EscalationLevel) => Promise<void>;
  onReactivate: (row: EscalationLevel) => Promise<void>;
  onResetSaveError: () => void;
}

/** The empty-return deadline levels (gitaumoses4/chukua#38). Client-side paging — the small-fixed-catalogue exception to server-side lists (CLAUDE.md → Lists and tables). */
export function EscalationLevelsPanel({
  levels,
  loading,
  saveError,
  saving,
  onCreate,
  onUpdate,
  onDeactivate,
  onReactivate,
  onResetSaveError,
}: EscalationLevelsPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<EscalationLevel | undefined>(undefined);

  function openCreate() {
    onResetSaveError();
    setEditing(undefined);
    setDrawerOpen(true);
  }

  function openEdit(row: EscalationLevel) {
    onResetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const columns = buildEscalationLevelColumns({ onEdit: openEdit, onDeactivate, onReactivate });
  const newButton = (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={openCreate}>
      New escalation level
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-muted">
        When an empty container&apos;s return deadline reaches a level, the job&apos;s owner and the operations managers are notified once.
      </p>
      <div className="flex justify-end">{newButton}</div>
      {loading && <TableSkeleton label="Loading escalation levels" />}
      {!loading && levels.length === 0 && (
        <EmptyState icon={BellRing} title="No escalation levels" description="Without a level, nobody is notified about empty-return deadlines.">
          {newButton}
        </EmptyState>
      )}
      {!loading && levels.length > 0 && <Table rowKey="id" dataSource={levels} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: 'max-content' }} />}
      <EscalationLevelFormDrawer
        open={drawerOpen}
        editing={editing}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
